/**
 * The editor tabs. Each one keeps its own colour so the sections read apart:
 * General, List groups, Gateways, Creative, Options, Verdicts, Auto-send.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Layers, MessageSquareText, RadioTower, Scale, Send, Settings, SlidersHorizontal, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { TAB_CHROME, type EditorTabKey } from './tabs/tab-chrome';

export type { EditorTabKey };

interface TabMeta {
  key: EditorTabKey;
  label: string;
  icon: LucideIcon;
  count?: 'groups' | 'pool';
  containerOnly?: boolean;
  /** Gateways belong to the master config. */
  masterOnly?: boolean;
}

const TABS: TabMeta[] = [
  { key: 'general', label: 'General & Slots', icon: Settings },
  {
    key: 'groups',
    label: 'List groups',
    icon: Layers,
    count: 'groups',
    containerOnly: true,
  },
  {
    key: 'gateways',
    label: 'Gateways',
    icon: RadioTower,
    count: 'pool',
    masterOnly: true,
  },
  { key: 'creative', label: 'Creative', icon: MessageSquareText },
  { key: 'options', label: 'Options', icon: SlidersHorizontal },
  { key: 'verdicts', label: 'Verdicts', icon: Scale },
  { key: 'autosend', label: 'Auto-send', icon: Send },
];

interface Props {
  value: EditorTabKey;
  /** A master has no List groups tab. */
  mode?: 'container' | 'master';
  routeTest?: boolean;
  groupCount: number;
  poolCount: number;
  /** Tabs whose config is still missing — they get an amber dot. */
  missingTabs?: EditorTabKey[];
  /** The layer this editor holds sends by itself (green dot). */
  autoSendOn?: boolean;
  onChange: (tab: EditorTabKey) => void;
}

const MissingDot: FC<{ show: boolean }> = ({ show }) =>
  show ? <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Setup incomplete" /> : null;

interface ButtonProps {
  meta: TabMeta;
  active: boolean;
  count?: number;
  missing: boolean;
  auto: boolean;
  onChange: (tab: EditorTabKey) => void;
}

const TabButton: FC<ButtonProps> = ({ meta, active, count, missing, auto, onChange }) => {
  const tone = TAB_CHROME[meta.key];
  const Icon = meta.icon;
  const badge = active ? tone.badge : 'bg-muted text-muted-foreground';
  return (
    <button
      type="button"
      data-tone={meta.key}
      data-active={active ? 'true' : 'false'}
      aria-pressed={active}
      className={cn('inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium', active ? tone.active : tone.idle)}
      onClick={() => onChange(meta.key)}
    >
      <Icon className={cn('h-3.5 w-3.5', tone.icon)} />
      {meta.label}
      {count != null ? <span className={cn('rounded-full px-1.5 text-[10px] font-semibold', badge)}>{count}</span> : null}
      <MissingDot show={missing} />
      {auto ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-label="Auto-send on" /> : null}
    </button>
  );
};

export const EditorTabs: FC<Props> = ({
  value,
  mode = 'container',
  routeTest = false,
  groupCount,
  poolCount,
  missingTabs = [],
  autoSendOn = false,
  onChange,
}) => (
  <div className="flex flex-wrap items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-1">
    {TABS.filter((tab) => (mode === 'container' ? !tab.masterOnly || routeTest : !tab.containerOnly)).map((meta) => (
      <TabButton
        key={meta.key}
        meta={routeTest && meta.key === 'gateways' ? { ...meta, label: 'Test gateways' } : meta}
        active={value === meta.key}
        count={meta.count === 'groups' ? groupCount : meta.count === 'pool' ? poolCount : undefined}
        missing={missingTabs.includes(meta.key)}
        auto={meta.key === 'autosend' && autoSendOn}
        onChange={onChange}
      />
    ))}
  </div>
);
